import type { Lesson } from "@/core/types";
import { formelAufgabe } from "../formelaufgabe";

export const lektionDynamischeArrays: Lesson = {
  id: "dynamische-arrays",
  title: "Dynamische Arrays: FILTER, SORTIEREN, EINDEUTIG",
  summary: "Eine Formel, viele Ergebniszellen – Auswertungen, die mitwachsen.",
  durationMinutes: 12,
  learningGoals: [
    "Verstehen, was ein Überlaufbereich ist",
    "Mit FILTER eine gefilterte Liste per Formel erzeugen",
    "Mit EINDEUTIG und SORTIEREN Listen verdichten und ordnen",
  ],
  steps: [
    {
      id: "ueberlauf",
      title: "Der Überlaufbereich",
      explanation: [
        "Seit Microsoft 365 kann eine Formel mehrere Zellen füllen. Sie steht nur in der " +
          "linken oberen Zelle; die Ergebnisse „laufen über“ in die Zellen darunter und " +
          "daneben. Ein dünner blauer Rahmen zeigt den Überlaufbereich.",
        "Steht im Weg schon etwas, meldet die Formel #ÜBERLAUF!. Die Lösung ist nicht, die " +
          "Formel zu kopieren, sondern den Platz freizuräumen.",
        "Auf einen Überlaufbereich verweist man mit dem Rautezeichen: =ANZAHL2(F2#) zählt " +
          "alle Ergebnisse, egal wie viele es gerade sind.",
      ],
      keyPoints: [
        "Eine Formel, viele Ergebniszellen.",
        "#ÜBERLAUF! = Platz freiräumen.",
        "F2# verweist auf den ganzen Überlaufbereich.",
      ],
    },
    {
      id: "filter",
      title: "FILTER",
      explanation: [
        "=FILTER(A2:D100;B2:B100=\"Nord\") liefert alle Zeilen, in denen in Spalte B „Nord“ " +
          "steht – als Formel, die sich neu berechnet, sobald sich die Daten ändern. Das " +
          "dritte Argument legt fest, was bei keinem Treffer erscheint: " +
          "=FILTER(…;…;\"keine Treffer\").",
        "Mehrere Bedingungen werden multipliziert (UND) oder addiert (ODER): " +
          "(B2:B100=\"Nord\")*(D2:D100>1000). Mit einer Tabelle als Quelle wächst die " +
          "Auswertung automatisch mit: =FILTER(Umsatz;Umsatz[Region]=G1).",
      ],
      keyPoints: [
        "=FILTER(Bereich;Bedingung;[wenn leer]).",
        "* = UND, + = ODER.",
        "Mit Tabelle als Quelle wächst das Ergebnis mit.",
      ],
      task: {
        instruction: "Erzeuge mit FILTER eine gefilterte Liste deiner Daten.",
        hint: "=FILTER(A2:D20;B2:B20=\"Nord\";\"keine Treffer\")",
        check: formelAufgabe([{ label: "Eine FILTER-Formel", funktionen: ["filter"] }], {
          success: "Eine Liste, die sich selbst filtert – ohne Filterschaltfläche.",
          failure: "Es gibt noch keine FILTER-Formel.",
          hint: "#KALK! bedeutet: kein Treffer und kein drittes Argument.",
        }),
      },
    },
    {
      id: "eindeutig-sortieren",
      title: "EINDEUTIG und SORTIEREN",
      explanation: [
        "EINDEUTIG liefert jede Ausprägung einer Spalte genau einmal: =EINDEUTIG(B2:B100) " +
          "ergibt die Liste aller Regionen – ohne Duplikate entfernen, ohne PivotTable. " +
          "SORTIEREN ordnet eine Liste: =SORTIEREN(EINDEUTIG(B2:B100)).",
        "Zusammen mit SUMMEWENN entsteht so eine kleine Auswertung, die neue Regionen " +
          "selbst aufnimmt: links =SORTIEREN(EINDEUTIG(Umsatz[Region])), rechts daneben " +
          "=SUMMEWENN(Umsatz[Region];F2#;Umsatz[Betrag]).",
      ],
      keyPoints: [
        "EINDEUTIG = jede Ausprägung einmal.",
        "SORTIEREN(Bereich;[Spalte];[1/-1]).",
        "Kombiniert mit SUMMEWENN und F2# eine mitwachsende Auswertung.",
      ],
      task: {
        instruction: "Erzeuge mit EINDEUTIG oder SORTIEREN eine Liste, etwa alle Regionen alphabetisch.",
        hint: "=SORTIEREN(EINDEUTIG(B2:B20))",
        check: formelAufgabe([{ label: "EINDEUTIG oder SORTIEREN", funktionen: ["eindeutig", "sortieren"] }], {
          success: "Die Liste verdichtet und ordnet sich selbst.",
          failure: "Es gibt noch keine EINDEUTIG- oder SORTIEREN-Formel.",
        }),
      },
    },
  ],
};
